import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, Button as MuiButton } from '@mui/material';
import { Business } from '@mui/icons-material';
import { EmptyState } from '@/components/ui';
import { useAuthStore } from '@/store/authStore';
import { authService } from '@/services/auth.service';
import { useToastStore } from '@/store/toastStore';

/**
 * Page Fiche société — redirige vers l’édition de l’entreprise de l’utilisateur connecté.
 * Si l’utilisateur n’a pas d’entreprise associée, affiche un message.
 */
export function SocietyPage(): JSX.Element {
  const navigate = useNavigate();
  const user = useAuthStore((state) => state.user);
  const setUser = useAuthStore((state) => state.setUser);
  const enterpriseId = user?.enterprise?.id ?? user?.enterpriseId ?? null;
  const hasRefetched = useRef(false);
  const [linking, setLinking] = useState(false);
  const { success, error: showError } = useToastStore();

  const isAdmin = user?.role === 'ADMIN';

  useEffect(() => {
    if (isAdmin && enterpriseId) {
      navigate(`/enterprises/${enterpriseId}/edit`, {
        state: { fromSociety: true },
        replace: true,
      });
    }
  }, [isAdmin, enterpriseId, navigate]);

  useEffect(() => {
    if (user?.id && !enterpriseId && !hasRefetched.current) {
      hasRefetched.current = true;
      authService.getMe().then(setUser).catch(() => {});
    }
  }, [user?.id, user?.enterprise?.id, setUser]);

  const handleRefresh = () => {
    authService.getMe().then(setUser).catch(() => {});
  };

  const handleLinkToDefiGourmand = async () => {
    setLinking(true);
    try {
      const updatedUser = await authService.linkMyEnterprise();
      setUser(updatedUser);
      success("Compte rattaché à Défi Gourmand. Redirection...");
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        'Impossible de rattacher le compte. L\'entreprise Défi Gourmand existe-t-elle en base ?';
      showError(msg);
    } finally {
      setLinking(false);
    }
  };

  if (enterpriseId && isAdmin) {
    return <Box component="span" sx={{ display: 'none' }} />;
  }

  if (enterpriseId && !isAdmin) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        <Typography variant="h2" sx={{ fontSize: '24px', fontWeight: 700 }}>
          Fiche société
        </Typography>
        <EmptyState
          icon={<Business />}
          title="Modification réservée aux administrateurs"
          description="Seuls les utilisateurs avec le rôle Administrateur peuvent modifier la fiche société."
        />
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Typography variant="h2" sx={{ fontSize: '24px', fontWeight: 700 }}>
        Fiche société
      </Typography>
      <EmptyState
        icon={<Business />}
        title="Aucune société associée"
        description="Votre compte n'est pas rattaché à une société. Cliquez sur « Rattacher à Défi Gourmand » pour associer votre compte à l'entreprise Défi Gourmand (si elle existe en base)."
        action={{
          label: linking ? 'Rattachement...' : 'Rattacher à Défi Gourmand',
          onClick: handleLinkToDefiGourmand,
          variant: 'primary',
        }}
      />
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: -2 }}>
        <MuiButton
          variant="text"
          size="small"
          onClick={handleRefresh}
          disabled={linking}
          sx={{ textTransform: 'none' }}
        >
          Vérifier à nouveau
        </MuiButton>
      </Box>
    </Box>
  );
}
